import {
  consultantKpiDefaultObjectives,
  type ConsultantKpi,
  type ConsultantKpiMetrics,
  type ConsultantKpiPeriod,
} from "@asap/shared/schemas/team-member-data";

const mockPld = {
  netMargin: 9240,
  netMarginRate: 14.2,
  revenue: 128500,
  period: "202607",
  netMarginTrend: [
    { period: "202511", netMargin: 7800 },
    { period: "202512", netMargin: 7280 },
    { period: "202601", netMargin: 7120 },
    { period: "202602", netMargin: 7640 },
    { period: "202603", netMargin: 7910 },
    { period: "202604", netMargin: 8060 },
    { period: "202605", netMargin: 8390 },
    { period: "202606", netMargin: 8980 },
    { period: "202607", netMargin: 9240 },
  ],
  status: "success" as const,
  errorKey: null,
};

const previousMockPld = {
  netMargin: 8980,
  netMarginRate: 16.1,
  revenue: 121400,
  period: "202606",
  netMarginTrend: [],
  status: "success" as const,
  errorKey: null,
};

const previousMonthKpi: ConsultantKpiMetrics = {
  period: "month",
  periodStartDate: "2026-06-01",
  periodEndDate: "2026-06-30",
  calls: {
    talentCallCount: 202,
    trend: [
      { startDate: "2026-06-01", talentCallCount: 58 },
      { startDate: "2026-06-08", talentCallCount: 61 },
      { startDate: "2026-06-15", talentCallCount: 39 },
      { startDate: "2026-06-22", talentCallCount: 44 },
    ],
  },
  planning: {
    temporaryWorkerCount: 52,
    fullTimeEquivalent: 45,
    averageCoefficient: 2.1,
    averageHourlyWage: 19.5,
  },
  missions: {
    newCompanyCount: 3,
    existingCompanyCount: 9,
  },
  contracts: {
    createdCount: 14,
    withFirstContractTalentCount: 4,
    withTalentPreviousEndAtLeast30DaysCount: 2,
    withTalentPreviousEndAtLeast120DaysCount: 2,
    newTalentPlacementCount: 2,
    existingTalentPlacementCount: 7,
    planningRetentionPercent: 91,
  },
  pld: previousMockPld,
};

const previousWeekKpi: ConsultantKpiMetrics = {
  period: "week",
  periodStartDate: "2026-07-20",
  periodEndDate: "2026-07-24",
  calls: {
    talentCallCount: 54,
    trend: [
      { startDate: "2026-07-20", talentCallCount: 14 },
      { startDate: "2026-07-21", talentCallCount: 11 },
      { startDate: "2026-07-22", talentCallCount: 10 },
      { startDate: "2026-07-23", talentCallCount: 12 },
      { startDate: "2026-07-24", talentCallCount: 7 },
    ],
  },
  planning: {
    temporaryWorkerCount: 51,
    fullTimeEquivalent: 44.6,
    averageCoefficient: 1.95,
    averageHourlyWage: 19.8,
  },
  missions: {
    newCompanyCount: 2,
    existingCompanyCount: 2,
  },
  contracts: {
    createdCount: 3,
    withFirstContractTalentCount: 2,
    withTalentPreviousEndAtLeast30DaysCount: 0,
    withTalentPreviousEndAtLeast120DaysCount: 1,
    newTalentPlacementCount: 0,
    existingTalentPlacementCount: 3,
    planningRetentionPercent: 85,
  },
  pld: previousMockPld,
};

const monthKpi: ConsultantKpi = {
  period: "month",
  periodStartDate: "2026-07-01",
  periodEndDate: "2026-07-31",
  calls: {
    talentCallCount: 200,
    trend: [
      { startDate: "2026-07-01", talentCallCount: 52 },
      { startDate: "2026-07-06", talentCallCount: 68 },
      { startDate: "2026-07-13", talentCallCount: 45 },
      { startDate: "2026-07-20", talentCallCount: 71 },
      { startDate: "2026-07-27", talentCallCount: 44 },
    ],
  },
  planning: {
    temporaryWorkerCount: 50,
    fullTimeEquivalent: 43.9,
    averageCoefficient: 1.92,
    averageHourlyWage: 20,
  },
  missions: {
    newCompanyCount: 4,
    existingCompanyCount: 10,
  },
  contracts: {
    createdCount: 16,
    withFirstContractTalentCount: 3,
    withTalentPreviousEndAtLeast30DaysCount: 3,
    withTalentPreviousEndAtLeast120DaysCount: 1,
    newTalentPlacementCount: 3,
    existingTalentPlacementCount: 8,
    planningRetentionPercent: 88,
  },
  pld: mockPld,
  objectives: consultantKpiDefaultObjectives.month,
  previousPeriod: previousMonthKpi,
};

const weekKpi: ConsultantKpi = {
  period: "week",
  periodStartDate: "2026-07-27",
  periodEndDate: "2026-07-31",
  calls: {
    talentCallCount: 48,
    trend: [
      { startDate: "2026-07-27", talentCallCount: 12 },
      { startDate: "2026-07-28", talentCallCount: 16 },
      { startDate: "2026-07-29", talentCallCount: 9 },
      { startDate: "2026-07-30", talentCallCount: 18 },
      { startDate: "2026-07-31", talentCallCount: 11 },
    ],
  },
  planning: {
    temporaryWorkerCount: 50,
    fullTimeEquivalent: 43.9,
    averageCoefficient: 1.92,
    averageHourlyWage: 20,
  },
  missions: {
    newCompanyCount: 1,
    existingCompanyCount: 3,
  },
  contracts: {
    createdCount: 4,
    withFirstContractTalentCount: 1,
    withTalentPreviousEndAtLeast30DaysCount: 1,
    withTalentPreviousEndAtLeast120DaysCount: 0,
    newTalentPlacementCount: 1,
    existingTalentPlacementCount: 2,
    planningRetentionPercent: 88,
  },
  pld: {
    ...mockPld,
    netMarginTrend: [
      { period: "2026-07-27", netMargin: 8600 },
      { period: "2026-07-28", netMargin: 8780 },
      { period: "2026-07-29", netMargin: 8910 },
      { period: "2026-07-30", netMargin: 9100 },
      { period: "2026-07-31", netMargin: 9240 },
    ],
  },
  objectives: consultantKpiDefaultObjectives.week,
  previousPeriod: previousWeekKpi,
};

export const createMockConsultantKpi = (period: ConsultantKpiPeriod): ConsultantKpi =>
  period === "week" ? weekKpi : monthKpi;
